import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import TeroumaLogo from "./ui/TeroumaLogo";

const DISMISS_KEY = "terouma_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Install Prompt — shows a non-intrusive banner suggesting users install
 * the app to their home screen. Auto-hides if dismissed in the last 7 days
 * or if the app is already installed (running in standalone mode).
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // If already installed (standalone display mode), don't show prompt
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return; // iOS

    // Don't show if dismissed recently
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const handler = (e) => {
      e.preventDefault(); // Don't show the default browser prompt
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
      setDeferredPrompt(null);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white border border-stone-200 shadow-2xl rounded-2xl p-4 z-50 animate-in slide-in-from-bottom">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-stone-50 rounded-xl flex items-center justify-center border border-stone-200">
          <TeroumaLogo size={32} showText={false} variant="icon" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-stone-900">
            Installer Terouma
          </h3>
          <p className="text-xs text-stone-600 mt-1">
            Accédez à votre tableau de bord en un clic, même hors ligne.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 bg-stone-900 text-white text-xs font-medium rounded-lg hover:bg-stone-800 transition-colors"
            >
              Installer
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-stone-600 text-xs font-medium rounded-lg hover:bg-stone-50 transition-colors"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-stone-400 hover:text-stone-600 transition-colors"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
